import {useEffect, useMemo, useRef, useState, useSyncExternalStore} from 'react'

import {getStore, StoreParams} from './useTransition.store'
import {
  AsyncCallback,
  CommitFunction,
  ErrorFunction,
  TransitionFunction,
  TransitionParams,
  TransitionResult,
  TransitionState,
} from './useTransition.types'

export function useTransition<TResult = unknown, TError = unknown>(
  callback?: AsyncCallback<TResult>,
  params: TransitionParams<TResult> = {},
): TransitionResult<TResult, TError> {
  const initialParams = useRef<StoreParams>({
    key: params.key || callback?.name,
    isPending: !!callback,
    updateStrategy: params.updateStrategy,
  })
  const [{subscribe, getState}, actions] = useMemo(() => getStore(initialParams.current), [])
  const asyncState = useSyncExternalStore(subscribe, getState)
  const [transitionState, setTransitionState] = useState<TransitionState<TResult, TError>>(() => ({
    promise: callback ? callback() : null,
  }))
  const startTransition = useMemo((): TransitionFunction<TResult, TError> => {
    let timeoutID: number | null = null

    return function _startTransition(callback, options = {}): void {
      const {timeoutMs, onCommit, onError} = options

      function _setTransitionState(): void {
        timeoutID = null
        setTransitionState({
          promise: callback(),
          onCommit,
          onError,
        })
      }

      if (timeoutID !== null) {
        clearTimeout(timeoutID)
      }

      if (timeoutMs === undefined) {
        return _setTransitionState()
      }

      timeoutID = window.setTimeout(_setTransitionState, timeoutMs)
    }
  }, [])

  const transitionRef = useRef(transitionState)
  transitionRef.current = transitionState

  useEffect(() => {
    const {promise, onCommit, onError} = transitionState
    const {pending, resolve, reject} = actions

    if (promise === null) {
      return
    }

    pending(true)

    // а вот финальный результат устанавливаем только для последнего промиса
    const commit = (
      callback: CommitFunction<TResult> | ErrorFunction<TError>,
      onFinish?: CommitFunction<TResult> | ErrorFunction<TError>,
    ) => {
      return (result: TResult | TError) => {
        if (promise === transitionRef.current.promise) {
          // @ts-expect-error
          callback(result)
          // @ts-expect-error
          onFinish && onFinish(result)
        }
      }
    }

    promise.then(commit(resolve, onCommit)).catch(commit(reject, onError))
  }, [actions, transitionState])

  // todo нет очищения стейта после анмаунта всех компонентов
  //      если будешь делать, то не забудь что один стейт может использовать несколько хуков
  //      то есть после анмаунта одного, не нужно сразу удалять стейт, вдруг его кто-то использует

  return [asyncState, startTransition]
}
